import { observer } from "mobx-react-lite";
import { Popup } from "react-map-gl/mapbox";
import { AnimatePresence, motion } from "framer-motion";
import { Pin, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MapPopupsStore } from "@/components/Map/MapPopups/MapPopupsStore";

const POPUP_ANIM = {
	initial: { opacity: 0, y: 6, scale: 0.98 },
	animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.1, ease: "easeOut" } },
	exit: { opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.1, ease: "easeIn" } },
};

interface MapPopupsProps {
	popupsStore: MapPopupsStore;
	renderPopup: (id: string) => React.ReactNode;
}

/**
 * Pure renderer for hover + pinned popups. State and event wiring live
 * outside (in the consumer's MapPopupsStore — see utils/wireMarkerPopups
 * for the standard hover/pin wiring helper).
 *
 * Must be rendered as a child of <Map> (react-map-gl <Popup> needs map context).
 */
export const MapPopups = observer(({ popupsStore, renderPopup }: MapPopupsProps) => {
	return (
		<AnimatePresence initial={false}>
			{popupsStore.visible.map(popup => {
				const isPinned = popup.mode === "pinned";
				const content = renderPopup(popup.id);
				if (!content) return null;
				return (
					<Popup
						className="popup-structure"
						key={popup.id}
						longitude={popup.lng}
						latitude={popup.lat}
						closeButton={false}
						closeOnClick={false}
						closeOnMove={false}
						anchor="bottom"
						maxWidth="280px"
						onClose={isPinned ? () => popupsStore.unpin(popup.id) : undefined}
					>
						<motion.div
							className="relative"
							variants={POPUP_ANIM}
							initial="initial"
							animate="animate"
							exit="exit"
							onMouseEnter={() => !isPinned && popupsStore.lockHover()}
							onMouseLeave={() => !isPinned && popupsStore.unlockHover(160)}
						>
							{isPinned && (
								<>
									<Pin
										className={cn(
											"absolute -left-1.5 -top-1.5 z-40 h-4 w-4 -rotate-[15deg]",
											"text-primary fill-current pointer-events-none",
											"[filter:drop-shadow(0_0.5px_1px_rgb(0_0_0_/_0.2))]",
										)}
										aria-hidden
									/>
									<button
										type="button"
										className={cn(
											"absolute -right-2 -top-2 z-40 inline-flex items-center justify-center",
											"h-6 w-6 rounded-full border bg-white shadow-md",
											"text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors",
											"cursor-pointer",
										)}
										onClick={() => popupsStore.unpin(popup.id)}
										aria-label="Close popup"
									>
										<X className="h-3.5 w-3.5" />
									</button>
								</>
							)}
							{content}
						</motion.div>
					</Popup>
				);
			})}
		</AnimatePresence>
	);
});
